import React, { useEffect, useState } from "react";
import API from "../../services/api";
import DataTable from "../../components/common/DataTable";
import { useToast } from "../../components/common/Toast";
import { FaEdit, FaTrash, FaDatabase } from "react-icons/fa";
import "../../styles/target.css";
import "../../styles/adminGlobal.css";

/* =========================================================
   Admin: Global Data Explorer
   Cross-table read/search/sort/edit/delete access to every
   table exposed by the backend's generic admin endpoints.
     GET    /api/admin/tables
     GET    /api/admin/tables/:table
     PUT    /api/admin/tables/:table/:id
     DELETE /api/admin/tables/:table/:id

   Columns are unknown ahead of time (each table is different),
   so this page renders columns dynamically from the keys of
   the first returned row, and the edit modal builds its form
   the same way.
========================================================= */
function GlobalDataExplorer() {
  const toast = useToast();

  const [tables, setTables] = useState([]);
  const [table, setTable] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 15;

  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/admin/tables")
      .then((res) => {
        setTables(res.data.tables || []);
        if (res.data.tables && res.data.tables.length) setTable(res.data.tables[0]);
      })
      .catch(() => toast.error("Failed to load table list"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (table) fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, page, search]);

  const fetchRecords = () => {
    setLoading(true);
    API.get(`/admin/tables/${table}`, { params: { search: search || undefined, page, limit } })
      .then((res) => {
        setRows(res.data.data || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toast.error(`Failed to load ${table}`))
      .finally(() => setLoading(false));
  };

  const handleTableChange = (e) => {
    setTable(e.target.value);
    setPage(1);
    setSearch("");
  };

  const primaryKey = (row) => row.id ?? Object.values(row)[0];

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({ ...row });
  };

  const handleEditFieldChange = (key, value) => {
    setEditForm((f) => ({ ...f, [key]: value }));
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();
    setSaving(true);

    // Don't send the primary key back as a field to update.
    const { id, ...body } = editForm;

    API.put(`/admin/tables/${table}/${primaryKey(editing)}`, body)
      .then(() => {
        toast.success("Record updated successfully");
        setEditing(null);
        fetchRecords();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to update record"))
      .finally(() => setSaving(false));
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete this record from "${table}"? This cannot be undone.`)) return;
    API.delete(`/admin/tables/${table}/${primaryKey(row)}`)
      .then(() => {
        toast.success("Record deleted");
        fetchRecords();
      })
      .catch(() => toast.error("Failed to delete record"));
  };

  const columnKeys = rows.length ? Object.keys(rows[0]) : [];
  const displayCols = columnKeys.slice(0, 6); // keep the table readable; edit modal shows everything

  const columns = [
    ...displayCols.map((key) => ({
      key,
      label: key.replace(/_/g, " "),
      render: (r) => {
        const val = r[key];
        if (val === null || val === undefined) return "-";
        if (typeof val === "string" && val.length > 60) return `${val.slice(0, 60)}...`;
        return String(val);
      },
    })),
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="row-actions">
          <button className="icon-btn" onClick={() => openEdit(r)} title="Edit">
            <FaEdit />
          </button>
          <button className="icon-btn danger" onClick={() => handleDelete(r)} title="Delete">
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="global-explorer-page">
      <div className="mytarget-header">
        <div>
          <h2>
            <FaDatabase /> Global Data Explorer
          </h2>
          <p>Search, edit or delete any record across the system</p>
        </div>
      </div>

      <div className="filters-bar">
        <select value={table} onChange={handleTableChange}>
          {tables.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input
          placeholder={`Search in ${table || "table"}...`}
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyIcon="🗃️"
        emptyText={`No records found in ${table || "this table"}`}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
      />

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal-box modal-box-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Edit record in {table}</h3>
            <form onSubmit={handleSaveEdit}>
              <div className="dynamic-form-grid">
                {Object.keys(editForm).map((key) => (
                  <div className="form-row" key={key}>
                    <label>{key.replace(/_/g, " ")}</label>
                    <input
                      value={editForm[key] === null || editForm[key] === undefined ? "" : editForm[key]}
                      disabled={key === "id"}
                      onChange={(e) => handleEditFieldChange(key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalDataExplorer;
